import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Toolbar } from "primereact/toolbar";
import { InputNumber } from "primereact/inputnumber";
import ordemService from "../services/ordemService";
import { hasAnyAuthority, hasAuthority, hideProcessing, showProcessing, userSession } from "../utils/utils";
import { Paginator } from "primereact/paginator";
import InputPeriodo from "./components/inputPeriodo";
import { MultiSelect } from "primereact/multiselect";
import { hasAnyRole } from "../guards";

// Flag global para prevenir duplicação em StrictMode
let _ordensInitialized = false;

export default function Ordens() {
    const toast = useRef(null);
    const [ordensServicos, setOrdensServicos] = useState([]);
    const [displayDialog, setDisplayDialog] = useState(false);
    const [displayDeleteDialog, setDisplayDeleteDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState();
    const [sortOrder, setSortOrder] = useState(-1); // -1 = desc, 1 = asc
    const [inicio, setInicio] = useState(new Date(new Date().setDate(new Date().getDate() - 30))); //dia - 30
    const [fim, setFim] = useState(new Date());
    const [filtroStatus, setFiltroStatus] = useState(["EM CADASTRO", "ANALISE FINANCEIRA", "PAGAMENTO CONFIRMADO", "PARECER DE ACESSO", "ORÇAMENTO", "AGUARDANDO PAGAMENTO", "AGENDAMENTO", "AGENDADO", "REABERTA", "PÓS-VENDA"]);

    const [selectedItem, setSelectedItem] = useState(null);
    const [filtroTexto, setFiltroTexto] = useState("");

    const statusOptionsList = [
        { label: "Em Cadastro", value: "EM CADASTRO" },
        { label: "Revisão de Documentos", value: "REVISÃO DE DOCUMENTOS" },
        { label: "Análise Financeira", value: "ANALISE FINANCEIRA" },
        { label: "Pagamento Confirmado", value: "PAGAMENTO CONFIRMADO" },
        { label: "Parecer de Acesso", value: "PARECER DE ACESSO" },
        { label: "Orçamento", value: "ORÇAMENTO" },
        { label: "Aguardando Pagamento", value: "AGUARDANDO PAGAMENTO" },
        { label: "Agendamento", value: "AGENDAMENTO" },
        { label: "Agendado", value: "AGENDADO" },
        { label: "Reaberta", value: "REABERTA" },
        { label: "Pós-venda", value: "PÓS-VENDA" },
        { label: "Finalizada", value: "FINALIZADA" },
        { label: "Cancelada", value: "CANCELADA" }
    ]

    const prioridadeOptions = [
        { label: "Baixa", value: "baixa" },
        { label: "Média", value: "média" },
        { label: "Alta", value: "alta" },
        { label: "Urgente", value: "urgente" }
    ];

    const statusInicialPorPerfil = {
        "ADMINISTRADOR": ["EM CADASTRO", "REVISÃO DE DOCUMENTOS", "ANALISE FINANCEIRA", "PAGAMENTO CONFIRMADO", "PARECER DE ACESSO", "ORÇAMENTO", "AGUARDANDO PAGAMENTO", "AGENDAMENTO", "AGENDADO", "REABERTA", "PÓS-VENDA"],
        "AUXILIAR_ADMINISTRATIVO": ["PAGAMENTO CONFIRMADO", "PARECER DE ACESSO", "AGENDAMENTO", "AGENDADO", "REABERTA"],
        "FINANCEIRO": ["ANALISE FINANCEIRA", "AGUARDANDO PAGAMENTO"],
        "POS_VENDA": ["PÓS-VENDA"],
        "VENDEDOR": ["EM CADASTRO", "REVISÃO DE DOCUMENTOS", "AGUARDANDO PAGAMENTO", "AGENDAMENTO", "AGENDADO", "REABERTA"],
        "ORCAMENTISTA": ["ORÇAMENTO"]
    }

    // Carregar dados ao montar o componente - apenas uma vez
    useEffect(() => {
        if (!_ordensInitialized) {
            _ordensInitialized = true;
            console.log('[ORDENS] Já foi inicializado, ignorando');
            return;
        }
        
        const loadInitialData = async () => {
            showProcessing();
            try {

                var usuario = userSession();

                let listaStatusInicial = [];

                for (const index in usuario.autoridades) {
                    const authority = usuario.autoridades[index].authority || usuario.autoridades[index];

                    const statusPorPerfil = statusInicialPorPerfil[authority];
                    if (statusPorPerfil) {
                        listaStatusInicial = [...listaStatusInicial, ...statusPorPerfil];
                    }
                }

                // Remover duplicatas
                listaStatusInicial = [...new Set(listaStatusInicial)];

                await setFiltroStatus(listaStatusInicial);

                var filter = {
                    //inicio: inicio ? inicio.toISOString().split('T')[0] : null,
                    //fim: fim ? fim.toISOString().split('T')[0] : null,
                    status: listaStatusInicial,
                    texto: filtroTexto && filtroTexto.trim() !== "" ? filtroTexto.trim() : null
                };

                const response = await ordemService.getAll(0, 10, undefined, -1, filter);

                if (response.status === 200) {
                    setOrdensServicos(response.data.content || response.data);
                    setTotalRecords(response.data.totalElements || response.data.length);
                    setFirst(0);
                    setSortField(undefined);
                    setSortOrder(-1);
                }
            } catch (error) {
                toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: "Erro ao carregar ordens de serviço"
                });
            } finally {
                hideProcessing();
            }
        };
        
        loadInitialData();
    }, []);

    const search = async (page = 0, pageSize = rows, field = sortField, order = sortOrder) => {
        showProcessing();
        try {

            var filter = {
                //inicio: inicio ? inicio.toISOString().split('T')[0] : null,
                //fim: fim ? fim.toISOString().split('T')[0] : null,
                status: filtroStatus && filtroStatus.length > 0 ? filtroStatus : null,
                texto: filtroTexto && filtroTexto.trim() !== "" ? filtroTexto.trim() : null
            };

            const response = await ordemService.getAll(page, pageSize, field, order, filter);

            if (response.status === 200) {
                setOrdensServicos(response.data.content || response.data);
                setTotalRecords(response.data.totalElements || response.data.length);
                setFirst(page * pageSize);
                setSortField(field);
                setSortOrder(order);
            }

        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao carregar ordens de serviço"
            });
        } finally {
            hideProcessing();
        }
    };

    const editItem = (item) => {
        window.location.href = `/ordem/${item.id}`;
    };

    const getSeverity = (status) => {
        switch (status) {
            case "PAGAMENTO CONFIRMADO":
                return "success";
            case "progresso":
                return "warning";
            case "pendente":
                return "info";
            case "CANCELADA":
                return "danger";
            default:
                return "info";
        }
    };

    const getPrioridadeSeverity = (prioridade) => {
        switch (prioridade) {
            case "urgente":
                return "danger";
            case "alta":
                return "warning";
            case "média":
                return "info";
            case "baixa":
                return "success";
            default:
                return "info";
        }
    };

    const prioridadeBodyTemplate = (rowData) => {
        return (
            <Tag
                value={prioridadeOptions.find(p => p.value === rowData.prioridade)?.label}
                severity={getPrioridadeSeverity(rowData.prioridade)}
            />
        );
    };

    const dataBodyTemplate = (rowData, field) => {
        if (!rowData[field]) return "-";
        const data = new Date(rowData[field]);
        return data.toLocaleDateString("pt-BR");
    };

    const valorBodyTemplate = (rowData) => {
        if (!rowData.valorTotal) return "-";
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL"
        }).format(rowData.valor);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="action-buttons">
                <Button
                    icon="pi pi-search"
                    className="button-confirm"
                    tooltip="Ver"
                    onClick={() => editItem(rowData)}
                />
            </div>
        );
    };


    return (
        <div className="page-container">
            <Toast ref={toast} />

            <Card className="page-card">

                <div className="page-header">
                    <h2>Ordens de Serviço</h2>
                    {hasAnyAuthority(["ADMINISTRADOR", "VENDEDOR"]) && (
                        <Button 
                        label="Nova Ordem" 
                        icon="pi pi-plus"
                        onClick={() => window.location.href = "/ordem/nova"}
                        className="button-new"
                    />
                    )}
                    
                    
                </div>

                <div style={{ display: "flex", gap: "15px", marginBottom: "15px", flexWrap: "wrap" }}>
                    {/*
                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Período</label>
                        <InputPeriodo 
                            inicio={inicio}
                            fim={fim}
                            onChangePeriodo={(e) => {
                                setInicio(e.inicio);
                                setFim(e.fim);
                            }}
                        />
                    </div>
                    */}

                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Filtrar por Texto</label>
                        <InputText
                            value={filtroTexto}
                            onChange={(e) => setFiltroTexto(e.target.value)}
                            placeholder="ID, Vendedor ou Cliente"
                            style={{ width: "250px", height: "30px" }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    search(0, rows, sortField, sortOrder);
                                }
                            }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Status</label>
                        <MultiSelect
                            value={filtroStatus}
                            options={statusOptionsList}
                            onChange={(e) => setFiltroStatus(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            maxSelectedLabels={1}
                            selectedItemsLabel="{size} status selecionados"
                            panelHeaderTemplate={() => (
                                <></>
                            )}
                            style={{ minWidth: "200px"}}
                            placeholder="Filtrar por status"
                        />
                    </div>
                    <div>
                        <Button label="Buscar" icon="pi pi-search" onClick={() => search(0, rows, sortField, sortOrder)} className="button-confirm" style={{ height: "30px", marginTop: "18px" }}/>
                    </div>

                    
                </div>
                
                    

                    
                


                <DataTable
                    value={ordensServicos}
                    loading={loading}
                    dataKey="id"
                    className="datatable-responsive"
                    onSort={(e) => {
                        const newOrder = e.sortOrder === 1 ? 1 : -1;
                        search(0, rows, e.sortField, newOrder);
                    }}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} Ordens"
                    rowsPerPageOptions={[10, 25, 50]}
                >
                    <Column field="id" sortable header="ID" style={{ width: "80px", textAlign: "right" }} />
                    <Column field="cliente.nome" sortable header="Cliente" />
                    <Column field="nomeVendedor"sortable header="Vendedor" />
                    <Column
                        field="status"
                        sortable
                        header="Status"
                        style={{ width: "100px", textAlign: "center" }}
                    />
                    <Column
                        field="prioridade"
                        header="Prioridade"
                        sortable
                        body={prioridadeBodyTemplate}
                        style={{ width: "80px", textAlign: "center" }}
                    />
                    <Column
                        field="valor"
                        header="Valor"
                        body={valorBodyTemplate}
                        style={{ width: "100px", textAlign: "right" }}
                    />
                    <Column
                        body={actionBodyTemplate}
                        header="Ações"
                        style={{ width: "50px", textAlign: "center" }}
                        alignFrozen="right"
                    />
                </DataTable>
                <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPageChange={(e) => {
                        setFirst(e.first);
                        setRows(e.rows);
                        search(Math.floor(e.first / e.rows), e.rows, sortField, sortOrder);
                    }}
                />
            </Card>

        </div>
    );
}
